import createBasicModeHandlers from './basicMode';
import type { TCalculatorModeHandlerFactory } from '@src/types';
import {
  applyExponential,
  applyFactorial,
  applyPlusMinus,
  applyPower,
  applyReciprocal,
  insertCloseBracket,
  insertOpenBracket,
  wrapWithFunction,
  handleScientificBackspace,
} from '@src/features/calculator/utils';

const TRIG_FUNCTION_MAP: Record<string, string> = {
  sin: 'sin',
  cos: 'cos',
  tan: 'tan',
  csc: 'csc',
  sec: 'sec',
  cot: 'cot',
};

const createScientificModeHandlers: TCalculatorModeHandlerFactory = (actions, state, meta) => {

  const baseHandlers = createBasicModeHandlers(actions);

  const handleNumber = (value?: string) => {
    if (!value) return;

    const currentInput = state!.input;
    if (value === '(') {
      const newExpression = insertOpenBracket(currentInput);
      actions.updateInput(newExpression);
      return;
    }

    if (value === ')') {
      const newExpression = insertCloseBracket(currentInput);
      actions.updateInput(newExpression);
      return;
    }

    baseHandlers.handleNumber(value);
  };

  const handleOperator = (value?: string) => {
    if (!value) return;

    const currentInput = state!.input;
    if (value in TRIG_FUNCTION_MAP) {
      const newExpression = wrapWithFunction(currentInput, TRIG_FUNCTION_MAP[value]);
      actions.updateInput(newExpression);
      return;
    }

    if (value === '√') {
      const newExpression = wrapWithFunction(currentInput, 'sqrt');
      actions.updateInput(newExpression);
      return;
    }

    if (value === 'eˣ') {
      const newExpression = applyExponential(currentInput);
      actions.updateInput(newExpression);
      return;
    }

    if (value === '1/x') {
      const newExpression = applyReciprocal(currentInput);
      actions.updateInput(newExpression);
      return;
    }

    if (value === '±') {
      const newExpression = applyPlusMinus(currentInput);
      actions.updateInput(newExpression);
      return;
    }

    if (value === 'n!') {
      const newExpression = applyFactorial(currentInput);
      actions.updateInput(newExpression);
      return;
    }

    if (value === 'π') {
      const lastChar = currentInput.slice(-1);
      const needsMultiplication = /[0-9)]/.test(lastChar) || currentInput.endsWith('pi') || lastChar === '!';

      if (needsMultiplication && currentInput !== '0') {
        
      if (meta?.justCalculated) {
          actions.updateInput(currentInput + '×pi');
        } else {
          actions.insertToken('×pi');
        }

      } else {
        actions.insertToken('pi');
      }
      return;
    }

    if (value === 'x²') {
      const newExpression = applyPower(currentInput, 2);
      actions.updateInput(newExpression);
      return;
    }

    if (value === 'x³') {
      const newExpression = applyPower(currentInput, 3);
      actions.updateInput(newExpression);
      return;
    }

    baseHandlers.handleOperator(value);
  };

  const handleEquals = () => {
    baseHandlers.handleEquals();
  };

  const handleBackspace = () => {
    const currentInput = state!.input;
    const newExpression = handleScientificBackspace(currentInput);

    if (!newExpression) {
      actions.updateInput('0');
    } else {
      actions.updateInput(newExpression);
    }
  };

  return {
    ...baseHandlers,
    handleNumber,
    handleOperator,
    handleEquals,
    handleBackspace,
  };
};

export default createScientificModeHandlers;
